import { Node, mergeAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditorState,
} from '@tiptap/react';
import React from 'react';

import { Badge, Icon, cn } from '@eluve/components';

export type TemplateSectionAttributes = {
  sectionId: string | null;
  isRequired: boolean;
  isAiEnabled: boolean;
};

// Private Components

/**
 * Deliberately minimal chrome: from the outside the document reads as
 * plain rich text. The only affordance is a drag handle in the left
 * gutter (visible on hover). Section state (AI / required) is edited —
 * and surfaced — through the bubble menu on the section title.
 */
const TemplateSectionView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
}) => {
  // Highlight the whole section while its title holds the caret (the
  // section bubble menu is open), signalling the controls apply to the
  // entire section
  const isSectionActive = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor.isActive('sectionTitle')) {
        return false;
      }
      const pos = getPos();
      if (typeof pos !== 'number') {
        return false;
      }
      const { from } = currentEditor.state.selection;
      return from >= pos && from <= pos + node.nodeSize;
    },
  });

  return (
    <NodeViewWrapper
      className={cn(
        'group relative rounded-md py-2 pr-1.5 pl-7 transition-colors hover:bg-bg-secondary/40',
        {
          'bg-bg-indigo-subtle-primary ring-2 ring-bg-indigo-strong-primary ring-inset':
            isSectionActive,
        },
      )}
    >
      <span
        data-drag-handle
        contentEditable={false}
        // h-7 matches the title's text-lg line height; top-1.5 keeps the
        // handle's center on the title line under the card's py-2 padding
        // (handle top = padding − (h-7 − line-height)/2)
        className={cn(
          // Asymmetric fade: snappy ease-out in on hover, slow ~700ms
          // ease-in-out fade-out (duration + curve are both read from the
          // state being transitioned to — base = out, :hover = in)
          'absolute top-1.5 left-1.5 flex h-7 cursor-grab items-center text-text-tertiary opacity-0 transition-opacity duration-700 ease-in-out group-hover:opacity-100 group-hover:duration-150 group-hover:ease-out',
          { 'text-text-indigo opacity-100': isSectionActive },
        )}
        aria-label="Drag to reorder section"
      >
        <Icon name="GripVertical" size="md" />
      </span>
      <NodeViewContent />
    </NodeViewWrapper>
  );
};

// Public

/**
 * Flex title row: the editable title text on the left and, for required
 * sections, a "Required" chip aligned to the right. The required flag
 * lives on the parent templateSection, so the title reads it from its
 * parent at the title's position.
 */
const SectionTitleView: React.FC<NodeViewProps> = ({ editor, getPos }) => {
  const isRequired = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      const pos = getPos();
      if (typeof pos !== 'number') {
        return false;
      }
      const parent = currentEditor.state.doc.resolve(pos).parent;
      return (
        parent.type.name === 'templateSection' &&
        Boolean(parent.attrs.isRequired)
      );
    },
  });

  return (
    <NodeViewWrapper className="mb-1 flex items-center gap-2">
      <NodeViewContent className="min-w-0 flex-1 text-lg font-semibold text-text-primary" />
      {isRequired && (
        <Badge
          variant="secondary"
          contentEditable={false}
          className="shrink-0 select-none"
        >
          Required
        </Badge>
      )}
    </NodeViewWrapper>
  );
};

/**
 * Title line of a template section. Plain inline text (no marks) styled
 * as a heading, so typing a section title feels like writing a normal
 * document. Selecting or clicking into it surfaces the section bubble
 * menu (required / AI / delete).
 */
export const SectionTitle = Node.create({
  name: 'sectionTitle',
  content: 'inline*',
  marks: '',
  defining: true,

  parseHTML() {
    return [{ tag: 'div[data-section-title]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-section-title': '',
        class: 'mb-1 text-lg font-semibold text-text-primary',
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SectionTitleView);
  },
});

/**
 * Custom TipTap node representing one template section: a title line
 * followed by markdown-capable body content, with "generated by AI"
 * and "required" state carried as node attributes. Sections are
 * draggable to reorder. This node is the editor intermediary that
 * translates to/from a DynamicArtifactTemplate text block (see
 * templateSectionsDoc.ts). Groups are intentionally not supported in
 * this first version.
 */
export const TemplateSection = Node.create({
  name: 'templateSection',
  // Deliberately NOT in the 'block' group: section bodies accept 'block+',
  // and being a block would let sections nest inside each other (e.g. by
  // dragging one into another). The document references this node by name.
  content: 'sectionTitle block+',
  defining: true,
  isolating: true,
  draggable: true,

  addAttributes() {
    return {
      sectionId: { default: null },
      isRequired: { default: false },
      isAiEnabled: { default: true },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-template-section]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-template-section': '' }),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Enter on the title line moves into the section body instead of
      // splitting the title
      Enter: () => {
        const { state } = this.editor;
        const { $from } = state.selection;
        if ($from.parent.type.name !== 'sectionTitle') {
          return false;
        }
        return this.editor.commands.command(({ tr, dispatch }) => {
          if (dispatch) {
            const afterTitle = $from.after($from.depth);
            tr.setSelection(TextSelection.near(tr.doc.resolve(afterTitle), 1));
            tr.scrollIntoView();
          }
          return true;
        });
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateSectionView);
  },
});
